import { Injectable } from '@angular/core';
import { 
  Firestore, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  doc, 
  getDoc,
  setDoc,
  runTransaction,
  Timestamp,
  limit,
  DocumentData
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of, lastValueFrom } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ColoringPage, ColoringPageCollection } from '../interfaces/coloring-page.interface';
import { PhotoService } from './photo.service';
import { PointsService } from './points.service';
import { ChildrenService } from './children.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ColoringBookService {
  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private http: HttpClient,
    private photoService: PhotoService,
    private pointsService: PointsService,
    private childrenService: ChildrenService
  ) {
    console.log('🖌️🖌️🖌️ INITIALIZING COLORING BOOK SERVICE WITH RUNWARE AI 🖌️🖌️🖌️');
    console.log('🖌️ RUNWARE API ENDPOINT:', this.RUNWARE_API_URL);
    
    // Add a timestamp to ensure this is the latest version
    console.log('🖌️ SERVICE INITIALIZED AT:', new Date().toISOString());
  }
  
  // Runware AI API endpoint
  private readonly RUNWARE_API_URL = 'https://api.runware.ai/v1/image-inference';
  
  /**
   * Generate a coloring page image using Runware AI API
   */
  async generateColoringPageImage(prompt: string, childId: string, cost: number = 1): Promise<string> {
    console.log('🖌️ USING RUNWARE AI FOR COLORING PAGE GENERATION 🖌️');
    console.log('generateColoringPageImage: Generating coloring page with prompt:', prompt);
    console.log('generateColoringPageImage: Child ID:', childId);
    
    // Check if the user is authenticated with Firebase
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to generate a coloring page');
    }
    
    console.log('generateColoringPageImage: Current user:', currentUser.uid);
    console.log('generateColoringPageImage: Is current user the child?', currentUser.uid === childId);
    
    // Check points BEFORE generating the image
    console.log('generateColoringPageImage: Checking points for child ID:', childId);
    
    const userPoints = await this.getUserPoints(childId);
    console.log('generateColoringPageImage: Child has', userPoints, 'points, coloring page costs', cost);
    
    if (userPoints < cost) {
      console.error(`Insufficient points! Child has ${userPoints} points but needs ${cost}`);
      throw new Error('Insufficient points');
    }
    
    try {
      // Only call Runware AI API if the child has enough points
      console.log('generateColoringPageImage: Child has sufficient points, generating image with RUNWARE AI...');
      const imageData = await this.generateAIImage(prompt);
      
      return imageData;
    } catch (error) {
      console.error('Error generating coloring page image:', error);
      throw error;
    }
  }

  /**
   * Get the current points balance for a user
   */
  private async getUserPoints(childId: string): Promise<number> {
    console.log('getUserPoints: Getting points for childId:', childId);
    
    try {
      // Get the current authenticated user
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        console.error('getUserPoints: No authenticated user found');
        return 0;
      }
      
      console.log('getUserPoints: Current authenticated user:', currentUser.uid);
      console.log('getUserPoints: Is current user the child?', currentUser.uid === childId);
      
      // Determine if the current user is a child or parent
      const isCurrentUserChild = currentUser.uid === childId;
      
      // IMPORTANT: We need to check the points for the actual child, not the authenticated user
      // If parent is logged in, use the childId parameter
      // If child is logged in, use their own ID (currentUser.uid)
      const pointsCheckId = childId;
      console.log(`getUserPoints: Checking points for ID: ${pointsCheckId} (${isCurrentUserChild ? 'child is logged in' : 'parent is logged in'})`);
      // First check if the child has a points document in the points collection
      const pointsRef = doc(this.firestore, `points/${pointsCheckId}`);
      const pointsDoc = await getDoc(pointsRef);
      
      if (pointsDoc.exists()) {
        const data = pointsDoc.data();
        const points = data['balance'] || 0;
        console.log(`getUserPoints: Found ${points} points in points collection for ${pointsCheckId}`);
        return points;
      }
      
      // If no points document, check the child document as fallback
      const childRef = doc(this.firestore, `children/${pointsCheckId}`);
      const childDoc = await getDoc(childRef);
      
      if (childDoc.exists()) {
        const childData = childDoc.data();
        if (childData.hasOwnProperty('points')) {
          const points = childData['points'] || 0;
          console.log(`getUserPoints: Found ${points} points in children collection for ${pointsCheckId}`);
          return points;
        }
      }
      
      // If no points found, return default value for testing
      console.log(`getUserPoints: No points found for ${pointsCheckId}, using default value`);
      return 10; // Default to 10 straws for testing
    } catch (error) {
      console.error('Error getting user points:', error);
      return 10; // Default to 10 straws for testing in case of error
    }
  }

  /**
   * Save a generated coloring page to the user's collection
   */
  async saveColoringPageToCollection(imageData: string, prompt: string, childId: string, cost: number = 1): Promise<ColoringPage> {
    if (!childId) {
      throw new Error('Child ID is required');
    }
    
    // Check if the user is authenticated with Firebase
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to save a coloring page');
    }
    
    console.log('saveColoringPageToCollection: Current authenticated user:', currentUser.uid);
    console.log('saveColoringPageToCollection: Saving coloring page for child:', childId);
    
    // Determine if the current user is a child or parent
    const isCurrentUserChild = currentUser.uid === childId;
    console.log('saveColoringPageToCollection: Is current user the child?', isCurrentUserChild);
    
    try {
      // Upload image to Firebase Storage
      const timestamp = new Date();
      const imagePath = `coloringPages/${childId}/${timestamp.getTime()}.jpg`;
      const imageUrl = await this.photoService.uploadPhotoFromDataUrl(imageData, imagePath);
      
      // Save coloring page metadata to Firestore
      const coloringPagesRef = collection(this.firestore, 'coloringPages');
      
      // Create the coloring page data with the current user ID to satisfy Firestore security rules
      const coloringPageData = {
        childId: currentUser.uid, // Use authenticated user ID for Firestore security rules
        actualChildId: childId,   // Store the actual child ID as a separate field
        imageUrl,
        prompt,
        createdAt: timestamp,
        cost
      } as Omit<ColoringPage, 'id'>;
      
      const docRef = await addDoc(coloringPagesRef, coloringPageData);
      
      // Deduct points from the CHILD'S account, not the parent's
      const transactionDescription = isCurrentUserChild ? 
        'Generated a coloring page' : 
        `Generated a coloring page for ${childId}`;
      
      console.log('saveColoringPageToCollection: Deducting', cost, 'points with description:', transactionDescription);
      // Use ChildrenService to update points, which will also update the straw-balance in the top bar
      await this.childrenService.adjustPoints(childId, -cost, transactionDescription);
      
      return {
        id: docRef.id,
        ...coloringPageData
      };
    } catch (error) {
      console.error('Error saving coloring page:', error);
      throw error;
    }
  }
  
  /**
   * Generate a new coloring page using Runware AI and save it to the user's collection
   */
  async generateColoringPage(prompt: string, childId: string, cost: number = 1): Promise<ColoringPage> {
    const imageData = await this.generateColoringPageImage(prompt, childId, cost);
    return this.saveColoringPageToCollection(imageData, prompt, childId, cost);
  }
  
  /**
   * Get all coloring pages created by a specific child
   */
  getChildColoringPages(childId: string): Observable<ColoringPage[]> {
    if (!childId) {
      console.log('getChildColoringPages: No childId provided, returning empty array');
      return of([]);
    }
    
    console.log('getChildColoringPages: Querying coloring pages for childId:', childId);
    
    // Get the current authenticated user
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('getChildColoringPages: No authenticated user found');
      return of([]);
    }
    
    console.log('getChildColoringPages: Current authenticated user:', currentUser.uid);
    
    const coloringPagesRef = collection(this.firestore, 'coloringPages');
    
    // Create a query that will find coloring pages where:
    // 1. The childId matches the current authenticated user (parent) AND actualChildId matches the requested child
    // 2. OR the childId directly matches the requested child (for backward compatibility)
    const q = query(
      coloringPagesRef,
      where('childId', '==', currentUser.uid),
      where('actualChildId', '==', childId),
      orderBy('createdAt', 'desc')
    );
    
    // Execute the query
    return from(getDocs(q)).pipe(
      map(snapshot => {
        console.log('getChildColoringPages: Query successful, found', snapshot.docs.length, 'coloring pages');
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ColoringPage));
      }),
      catchError(error => {
        console.error('getChildColoringPages: Error querying coloring pages:', error);
        // If there's an error, try the fallback query for backward compatibility
        console.log('getChildColoringPages: Trying fallback query with direct childId match');
        
        const fallbackQuery = query(
          coloringPagesRef,
          where('childId', '==', childId),
          orderBy('createdAt', 'desc')
        );
        
        return from(getDocs(fallbackQuery)).pipe(
          map(snapshot => {
            console.log('getChildColoringPages: Fallback query successful, found', snapshot.docs.length, 'coloring pages');
            return snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as ColoringPage));
          }),
          catchError(fallbackError => {
            console.error('getChildColoringPages: Error in fallback query:', fallbackError);
            // If the collection doesn't exist yet, return an empty array instead of an error
            if (fallbackError.code === 'permission-denied') {
              console.log('getChildColoringPages: Permission denied, the collection might not exist yet');
              return of([]);
            }
            throw fallbackError;
          })
        );
      })
    );
  }

  /**
   * Get all coloring pages from all children
   * This is used for the screen saver to display random coloring pages
   */
  getCommunityColoringPages(): Observable<(ColoringPage & { creatorName?: string })[]> {
    // Get the current authenticated user
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('getCommunityColoringPages: No authenticated user found');
      return of([]);
    }
    
    console.log('getCommunityColoringPages: Getting coloring pages for all children');
    
    const coloringPagesRef = collection(this.firestore, 'coloringPages');
    
    // Create a query that will find all coloring pages
    // Order by createdAt to get the newest first
    const q = query(
      coloringPagesRef,
      orderBy('createdAt', 'desc'),
      limit(50) // Limit to 50 most recent coloring pages for performance
    );
    
    // Execute the query
    return from(getDocs(q)).pipe(
      map(snapshot => {
        console.log('getCommunityColoringPages: Query successful, found', snapshot.docs.length, 'coloring pages');
        return snapshot.docs.map(doc => {
          const data = doc.data() as DocumentData;
          // Convert Firestore timestamp to Date
          const createdAt = data['createdAt'] instanceof Timestamp ? 
            (data['createdAt'] as Timestamp).toDate() : 
            data['createdAt'];
          
          return {
            id: doc.id,
            ...data,
            createdAt
          } as ColoringPage;
        });
      }),
      catchError(error => {
        console.error('getCommunityColoringPages: Error querying coloring pages:', error);
        return of([]);
      }),
      // Join with children service to get the child names
      switchMap((coloringPages: ColoringPage[]) => {
        if (coloringPages.length === 0) {
          return of([]);
        }
        
        // Get all unique child IDs from the coloring pages
        const childIds = [...new Set(coloringPages.map((page: ColoringPage) => 
          // Use actualChildId if available, otherwise use childId
          page.actualChildId || page.childId
        ))];
        
        // Get all children at once
        return this.childrenService.getChildren().pipe(
          map(children => {
            // Filter children to only those in our childIds list
            const relevantChildren = children.filter(child => childIds.includes(child.id));
            
            // Create a map of child ID to child name for quick lookup
            const childMap = new Map(relevantChildren.map(child => [child.id, child.displayName]));
            
            // Add the child name to each coloring page
            return coloringPages.map((page: ColoringPage) => {
              const childId = page.actualChildId || page.childId;
              return {
                ...page,
                creatorName: childMap.get(childId) || 'Unknown Child'
              };
            });
          })
        );
      })
    );
  }

  private async generateAIImage(prompt: string): Promise<string> {
    console.log('🖌️ GENERATING COLORING PAGE WITH RUNWARE AI 🖌️');
    
    try {
      // Check if we have a Runware API key configured
      if (!environment.runware?.apiKey) {
        console.log('No Runware API key provided in environment. Using placeholder image.');
        return this.getRandomPlaceholderImage(prompt);
      }
      
      // Get the API key and trim any whitespace
      const apiKey = environment.runware.apiKey.trim();
      
      // Create headers object with API key
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      });
      
      // Generate a unique taskUUID for this request
      const taskUUID = crypto.randomUUID();
      
      // Prepare the request payload according to the provided Runware API format
      const payload = [{
        taskType: 'imageInference',
        taskUUID: taskUUID,
        positivePrompt: `Create a simple black and white line drawing coloring page for toddlers and preschoolers. The image should feature a ${prompt} with very thick, bold outlines and minimal details. Use only simple shapes - circles for eyes, basic curves for the body, and no intricate patterns or small elements. The lines should be thick enough for small hands holding chunky crayons. Leave plenty of white space inside each section for easy coloring. No shading, textures, or complex details - just clean, simple outlines that create large areas to color.`,
        negativePrompt: 'nsfw, 3d, color, blurry',
        height: 1024,
        width: 1024,
        steps: 30,
        model: 'civitai:139562@798204',
        lora: [
          {
            model: 'civitai:136348@177996',
            weight: 1
          }
        ],
        numberResults: 1
      }];
      
      // Send the HTTP request to Runware
      const response$ = this.http.post<any>(
        this.RUNWARE_API_URL, 
        payload, 
        { 
          headers, 
          observe: 'response',
          responseType: 'json'
        }
      );
      
      // Wait for the response
      const fullResponse = await lastValueFrom(response$);
      const response = fullResponse.body;
      
      // Extract the image data from the response
      if (!response || !response.data || response.data.length === 0) {
        throw new Error('No response data received from Runware');
      }
      
      // Check for different output types and extract accordingly
      const result = response.data[0];
      
      if (result.imageDataURI) {
        return result.imageDataURI;
      }
      
      if (result.imageBase64Data) {
        return `data:image/png;base64,${result.imageBase64Data}`;
      }
      
      if (result.imageURL) {
        return result.imageURL;
      }
      
      // If we get here, we don't have any usable image data
      throw new Error('No image data in the Runware AI response');
    } catch (error) {
      console.error('Error generating AI image with Runware:', error);
      return this.getRandomPlaceholderImage(prompt);
    }
  }
  
  private getRandomPlaceholderImage(prompt: string): string {
    // Create a simple hash of the prompt to get a consistent color
    const hash = prompt.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // Generate a color based on the hash
    const hue = hash % 360;
    
    // Create a colored square as a placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill background with white for coloring page
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw black outlines for a coloring page
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 3;
      
      // Draw some simple shapes based on the prompt
      const shapeType = hash % 4;
      
      if (shapeType === 0) {
        // Circle
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 3, 0, Math.PI * 2);
        ctx.stroke();
      } else if (shapeType === 1) {
        // Square
        ctx.strokeRect(canvas.width / 4, canvas.height / 4, canvas.width / 2, canvas.height / 2);
      } else if (shapeType === 2) {
        // Triangle
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2, canvas.height / 4);
        ctx.lineTo(canvas.width / 4, canvas.height * 3 / 4);
        ctx.lineTo(canvas.width * 3 / 4, canvas.height * 3 / 4);
        ctx.closePath();
        ctx.stroke();
      } else {
        // Star
        const spikes = 5;
        const outerRadius = canvas.width / 3;
        const innerRadius = canvas.width / 6;
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;
        
        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        
        for (let i = 0; i < spikes; i++) {
          ctx.lineTo(cx + Math.cos(rot) * outerRadius, cy + Math.sin(rot) * outerRadius);
          rot += step;
          ctx.lineTo(cx + Math.cos(rot) * innerRadius, cy + Math.sin(rot) * innerRadius);
          rot += step;
        }
        
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
        ctx.stroke();
      }
      
      return canvas.toDataURL('image/png');
    }
    
    // Fallback to a simple white square if canvas is not supported
    return 'data:image/png;base64,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';
  }
}
